import type { ReactNode } from "react";

import { DataTable, KpiCard } from "../../../../shared";
import { KPI_CARD_BACKGROUND } from "../../../../../utils/Constants";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../../../utils/dateService";
import type {
  UserPromotion,
  UserPromotionsKpis,
} from "../userPromotionsTypes";
import styles from "../UserPromotions.module.css";

type UserPromotionsTabProps = {
  promotions: UserPromotion[];
  kpis: UserPromotionsKpis;
  isLoading: boolean;
  dateFilter: ReactNode;
};

const formatDateTime = (value: string | null): string =>
  value
    ? convertToUserTimezone(value, "MM/DD/YYYY hh:mm A")
    : AppStrings.PAM_USER_PROMOTIONS_NOT_APPLICABLE;

const formatMoneyType = (moneyType: string | null): string => {
  if (moneyType === "sportsbook_free_cash") {
    return AppStrings.PAM_AFFILIATE_PROMO_REWARD_SPORTSBOOK_FREE_CASH;
  }
  if (moneyType === "casino_cash") {
    return AppStrings.CASINO_CASH;
  }
  return AppStrings.PAM_USER_PROMOTIONS_NOT_APPLICABLE;
};

const formatClaimedAmount = (amount: number | null): string =>
  amount === null
    ? AppStrings.PAM_USER_PROMOTIONS_NOT_APPLICABLE
    : amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const formatList = (values: string[] | undefined): string => {
  const joined = (values ?? []).join(" / ");
  return joined || AppStrings.PAM_USER_PROMOTIONS_NOT_APPLICABLE;
};

const columns = [
  {
    title: AppStrings.PAM_USER_PROMOTIONS_SENT_AT,
    dataIndex: "sentAt",
    key: "sentAt",
    width: 190,
    render: (value: string) => formatDateTime(value),
  },
  {
    title: AppStrings.PAM_USER_PROMOTIONS_PROMO_ID,
    dataIndex: "promoId",
    key: "promoId",
    width: 130,
  },
  {
    title: AppStrings.PROMO_NAME,
    dataIndex: "promoName",
    key: "promoName",
    width: 180,
  },
  {
    title: AppStrings.DESCRIPTION,
    dataIndex: "description",
    key: "description",
    width: 240,
  },
  {
    title: AppStrings.PAM_USER_PROMOTIONS_PROMO_TYPES,
    dataIndex: "promoTypes",
    key: "promoTypes",
    width: 210,
    render: (types: string[] | undefined) => formatList(types),
  },
  {
    title: AppStrings.PAM_USER_PROMOTIONS_POTENTIAL_BONUSES,
    dataIndex: "potentialBonuses",
    key: "potentialBonuses",
    width: 260,
    render: (values: string[] | undefined) => formatList(values),
  },
  {
    title: AppStrings.STATUS,
    dataIndex: "status",
    key: "status",
    width: 130,
    ellipsis: false,
    render: (status: UserPromotion["status"]) => (
      <span className={`${styles.status} ${styles[status]}`}>
        {status === "claimed"
          ? AppStrings.CLAIMED
          : AppStrings.UNCLAIMED}
      </span>
    ),
  },
  {
    title: AppStrings.TIME_CLAIMED,
    dataIndex: "claimedAt",
    key: "claimedAt",
    width: 190,
    render: (value: string | null) => formatDateTime(value),
  },
  {
    title: AppStrings.AMOUNT,
    dataIndex: "claimedAmount",
    key: "claimedAmount",
    width: 130,
    render: (value: number | null) => formatClaimedAmount(value),
  },
  {
    title: AppStrings.MONEY_TYPE,
    dataIndex: "moneyType",
    key: "moneyType",
    width: 190,
    render: (value: string | null) => formatMoneyType(value),
  },
];

function UserPromotionsTab({
  promotions,
  kpis,
  isLoading,
  dateFilter,
}: UserPromotionsTabProps) {
  return (
    <div
      id="user-promotions-panel"
      role="tabpanel"
      aria-labelledby="user-promotions-tab"
      className={styles.tabPanel}
    >
      <div className={styles.summaryRow}>
        <div className={styles.kpis}>
          <KpiCard
            title={AppStrings.PAM_USER_PROMOTIONS_RECEIVED}
            value={kpis.received}
            maximumFractionDigits={0}
            hidePercentage
            isApiRunning={isLoading}
            currencySymbol=""
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            title={AppStrings.PAM_USER_PROMOTIONS_CLAIMED_COUNT}
            value={kpis.claimed}
            maximumFractionDigits={0}
            hidePercentage
            isApiRunning={isLoading}
            currencySymbol=""
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            title={AppStrings.PAM_USER_PROMOTIONS_SPORTSBOOK_CASH_CLAIMED}
            value={kpis.sportsbookFreeCashClaimed}
            hidePercentage
            isApiRunning={isLoading}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            title={AppStrings.PAM_USER_PROMOTIONS_CASINO_CASH_CLAIMED}
            value={kpis.casinoCashClaimed}
            hidePercentage
            isApiRunning={isLoading}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        {dateFilter}
      </div>

      <div className={styles.tableContainer}>
        <DataTable
          id="user-promotions-table"
          dataSource={promotions}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          scrollPreset="xUltraWide"
          locale={{ emptyText: AppStrings.PAM_USER_PROMOTIONS_EMPTY }}
          data-testid="user-promotions-table"
        />
      </div>
    </div>
  );
}

export default UserPromotionsTab;
